import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useHelpRequests } from '@/modules/help'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { HelpDrawer } from './HelpDrawer'
import { HELP_INDEX, type HelpIntent } from './helpIntent'
import { SupportMessenger } from './SupportMessenger'
import { SupportToast, type SupportToastData } from './SupportToast'

interface HelpSurfaceProps {
  open: boolean
  /** What the drawer opens onto (the layout resets it to the index on every
   *  plain open). A new intent while Help is up swaps back to the drawer. */
  intent?: HelpIntent
  onOpen: () => void
  onClose: () => void
}

type Mode = 'drawer' | 'messenger'

// The drawer's exit takes the next duration down from its 320ms entrance
// (design system Motion); keep in step with `--dur-med` on `.drawerClosing`.
const EXIT_MS = 240

/**
 * The one help surface (plans/help-support-status.md, Attio reference in
 * references/helpcenter/): the layout's `helpOpen` flag opens the light Help
 * drawer; "Support" inside it swaps to the floating messenger. Also owns the
 * support-reply toast — it watches the 15s list poll (the same query the
 * messenger renders from, so no extra polling) and pops when unread support
 * messages arrive while the messenger isn't looking at that thread.
 */
export function HelpSurface({ open, intent = HELP_INDEX, onOpen, onClose }: HelpSurfaceProps) {
  const [mode, setMode] = useState<Mode>('drawer')
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  // Every open mounts a fresh drawer. A closed drawer stays mounted, inert,
  // for its exit (the messenger has none); reopening mid-exit replaces it.
  const [session, setSession] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setSession((n) => n + 1)
    setLeaving(!open && mode === 'drawer')
  }
  useEffect(() => {
    if (!leaving) return
    const timer = window.setTimeout(() => setLeaving(false), reduced ? 0 : EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [leaving, reduced])
  // Set when the messenger should land directly on a thread (toast click).
  const [initialThreadId, setInitialThreadId] = useState<string | null>(null)
  const [toast, setToast] = useState<SupportToastData | null>(null)

  // Track what the messenger is currently showing so the toast can stay
  // quiet when the reply is already on screen.
  const viewingThreadRef = useRef<string | null>(null)
  const openRef = useRef(open)
  const modeRef = useRef<Mode>(mode)
  openRef.current = open
  modeRef.current = mode

  // Reopening always starts at the drawer (matches the reference flow).
  useEffect(() => {
    if (!open) {
      setMode('drawer')
      setInitialThreadId(null)
      viewingThreadRef.current = null
    }
  }, [open])

  // A fresh request (a page asking for a tutorial while the messenger is up)
  // lands in the drawer, which then shows what was asked for.
  useEffect(() => {
    setMode('drawer')
  }, [intent])

  const listQuery = useHelpRequests('account')

  // ── Reply toast: diff-detect the unread total rising ──────────────────
  const prevUnreadRef = useRef<number | null>(null)
  useEffect(() => {
    const items = listQuery.data
    if (!items) return
    const totalUnread = items.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0)
    const prev = prevUnreadRef.current
    prevUnreadRef.current = totalUnread
    if (prev === null || totalUnread <= prev) return

    // Newest unread thread where the last word was support's.
    const candidate = items
      .filter((r) => r.unreadCount > 0 && r.lastMessageFromSupport)
      .sort((a, b) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? ''))[0]
    if (!candidate) return
    // Already reading that thread in the open messenger → no toast.
    if (
      openRef.current &&
      modeRef.current === 'messenger' &&
      viewingThreadRef.current === candidate.id
    ) {
      return
    }
    setToast({
      requestId: candidate.id,
      preview: candidate.lastMessagePreview ?? 'New reply from the team',
      at: candidate.lastMessageAt ?? new Date().toISOString(),
    })
  }, [listQuery.data])

  const openMessenger = useCallback(() => setMode('messenger'), [])

  const handleToastOpen = useCallback(
    (requestId: string) => {
      setToast(null)
      setInitialThreadId(requestId)
      setMode('messenger')
      onOpen()
    },
    [onOpen],
  )

  const handleThreadViewed = useCallback((threadId: string | null) => {
    viewingThreadRef.current = threadId
  }, [])

  return (
    <>
      {(open ? mode === 'drawer' : leaving) && (
        <HelpDrawer
          key={session}
          intent={intent}
          closing={!open}
          onClose={onClose}
          onOpenMessenger={openMessenger}
        />
      )}
      {open && mode === 'messenger' && (
        <SupportMessenger
          onClose={onClose}
          initialThreadId={initialThreadId}
          onThreadViewed={handleThreadViewed}
        />
      )}
      {toast &&
        createPortal(
          <SupportToast
            toast={toast}
            onOpen={handleToastOpen}
            onDismiss={() => setToast(null)}
          />,
          document.body,
        )}
    </>
  )
}
